import { PracticeTemplateDetail, ProgramBenchmarkName, ProgramTask } from '@cibo/core'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import { Box, Button, Divider, IconButton, Stack, TextField, Typography } from '@mui/material'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import { uniq } from 'ramda'
import { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TraitDataGrid } from '../TraitDataGrid'
import { useTraitColumns } from '../useTraitColumns'
import { TemplateEditorFooter } from './TemplateEditorFooter'
import { TemplateEditorTableCell } from './TemplateEditorTableCell'

interface TemplateEditorMainProps {
  task: ProgramTask<{ benchmarkName: ProgramBenchmarkName }>
  draftDetails: PracticeTemplateDetail[]
  years: string[]
  onValueSave: (
    traitId: string,
    year: number,
    filledValues: { value?: unknown; input?: unknown; result?: unknown }
  ) => void
  onValueDelete: (traitId: string, year: number) => void
  onAddPractice: (traitId: string) => void
  onPrePopulate: () => void
  availableTraitIds: string[]
  fieldHasData: boolean
  canEdit: boolean
  canChangeOwnerScope: boolean
  canDelete: boolean
  isDeleting: boolean
  isSaving: boolean
  isDirty: boolean
  draftName: string
  nameError: string | null
  selectedId: string | 'new'
  onDraftNameChange: (name: string) => void
  onConfirmName: () => boolean
  onDelete: () => void
  onClose: () => void
  onSave: () => void
  ownerScope: 'farmer' | 'all'
  onOwnerScopeChange: (scope: 'farmer' | 'all') => void
  shouldFocusName?: boolean
  onShouldFocusNameHandled?: () => void
}

export const TemplateEditorMain = ({
  task,
  draftDetails,
  years,
  onValueSave,
  onValueDelete,
  onAddPractice,
  onPrePopulate,
  availableTraitIds,
  fieldHasData,
  canEdit,
  canChangeOwnerScope,
  canDelete,
  isDeleting,
  isSaving,
  isDirty,
  draftName,
  nameError,
  selectedId,
  onDraftNameChange,
  onConfirmName,
  onDelete,
  onClose,
  onSave,
  ownerScope,
  onOwnerScopeChange,
  shouldFocusName,
  onShouldFocusNameHandled,
}: TemplateEditorMainProps) => {
  const { t } = useTranslation([
    'traits',
    '@cibo/programs/TemplateEditorModal',
    '@cibo/programs/TaskEditorTable',
  ])
  const [_forceRender, setForceRender] = useState(0)
  const [isNameEditing, setIsNameEditing] = useState(selectedId === 'new')
  const [pendingFocus, setPendingFocus] = useState(false)
  const nameInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setIsNameEditing(selectedId === 'new')
  }, [selectedId])

  useEffect(() => {
    if (shouldFocusName) {
      setIsNameEditing(true)
      setPendingFocus(true)
      onShouldFocusNameHandled?.()
    }
  }, [shouldFocusName, onShouldFocusNameHandled])

  useEffect(() => {
    if (pendingFocus && nameInputRef.current) {
      nameInputRef.current.focus()
      setPendingFocus(false)
    }
  }, [pendingFocus])

  const hasPractices = draftDetails.length > 0
  const showPrePopulate = canEdit && !hasPractices && fieldHasData

  const handleNameKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (onConfirmName()) setIsNameEditing(false)
    } else if (e.key === 'Escape' && selectedId !== 'new') {
      setIsNameEditing(false)
    }
  }

  const handleNameConfirm = () => {
    if (onConfirmName()) setIsNameEditing(false)
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
      setForceRender(prev => prev + 1)
    }, 50)
    return () => clearTimeout(timer)
  }, [hasPractices, years.length])

  const rows = useMemo(() => {
    const traitIds = uniq(draftDetails.map(d => d.traitId))
    return traitIds.map(traitId => {
      const row: { id: string; traitId: string; [key: string]: any } = { id: traitId, traitId }
      years.forEach(year => {
        const d = draftDetails.find(d => d.traitId === traitId && d.year === Number(year))
        const cellValue: Record<string, unknown> = {}
        if (d?.input !== undefined) cellValue.input = d.input
        if (d?.result !== undefined) cellValue.result = d.result
        if (d?.value !== undefined) cellValue.value = d.value
        row[year] = Object.keys(cellValue).length ? cellValue : undefined
      })
      return row
    })
  }, [draftDetails, years])

  const renderYearCell = useCallback(
    (_year: string) => (params: GridRenderCellParams) =>
      (
        <TemplateEditorTableCell
          task={task}
          canEdit={canEdit}
          onValueSave={onValueSave}
          onValueDelete={onValueDelete}
          {...params}
        />
      ),
    [task, canEdit, onValueSave, onValueDelete]
  )

  const columns = useTraitColumns({
    traitIdHeaderName: t('practice', { ns: '@cibo/programs/TemplateEditorModal' }),
    years,
    renderYearCell,
  })

  return (
    <Stack sx={{ flex: 1, overflow: 'hidden' }}>
      <Stack direction="row" alignItems="center" gap={1} sx={{ px: 2, py: 1, minHeight: 48 }}>
        {isNameEditing ? (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ width: '50%', minWidth: 0 }}
          >
            <TextField
              autoFocus
              inputRef={nameInputRef}
              size="small"
              label={t('templateName', { ns: '@cibo/programs/TemplateEditorModal' })}
              value={draftName}
              onChange={e => onDraftNameChange(e.target.value)}
              onKeyDown={handleNameKeyDown}
              error={!!nameError}
              helperText={nameError ?? undefined}
              disabled={!canEdit}
              sx={{ flex: 1 }}
              slotProps={{ htmlInput: { maxLength: 100 } }}
              data-testid="template-name-input"
            />
            <Button
              size="small"
              onClick={handleNameConfirm}
              data-testid="template-name-confirm-button"
            >
              {t('save', { ns: '@cibo/programs/TemplateEditorModal' })}
            </Button>
          </Stack>
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ width: '50%', minWidth: 0 }}
          >
            <Typography
              data-testid="template-name-display"
              sx={{
                fontSize: 16,
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {draftName || t('newTemplate', { ns: '@cibo/programs/TemplateEditorModal' })}
            </Typography>
            {canEdit && (
              <IconButton
                size="small"
                onClick={() => setIsNameEditing(true)}
                aria-label={t('editTemplateName', { ns: '@cibo/programs/TemplateEditorModal' })}
                data-testid="template-name-edit-button"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        )}
        <Box sx={{ flex: 1 }} />
        <IconButton
          size="small"
          onClick={onClose}
          aria-label={t('close', { ns: '@cibo/programs/TemplateEditorModal' })}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <Divider />

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {hasPractices ? (
          <TraitDataGrid rows={rows} columns={columns} hideFooter />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              minHeight: 160,
              m: 2,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('emptyState', { ns: '@cibo/programs/TemplateEditorModal' })}
            </Typography>
            {showPrePopulate && (
              <Button
                variant="outlined"
                size="small"
                data-testid="template-pre-populate-button"
                onClick={onPrePopulate}
              >
                {t('prePopulate', { ns: '@cibo/programs/TemplateEditorModal' })}
              </Button>
            )}
          </Box>
        )}
      </Box>

      <TemplateEditorFooter
        canEdit={canEdit}
        canDelete={canDelete}
        canChangeOwnerScope={canChangeOwnerScope}
        isDeleting={isDeleting}
        isDirty={isDirty}
        isSaving={isSaving}
        selectedId={selectedId}
        draftName={draftName}
        hasPractices={hasPractices}
        onSave={onSave}
        onDelete={onDelete}
        ownerScope={ownerScope}
        onOwnerScopeChange={onOwnerScopeChange}
        availableTraitIds={availableTraitIds}
        onAddPractice={onAddPractice}
      />
    </Stack>
  )
}
